import MomentUtils from "@date-io/moment";
import { AppBar, Divider, Drawer, Hidden, IconButton, Theme, Toolbar } from '@material-ui/core';
import { Menu } from '@material-ui/icons';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { createStyles, makeStyles } from '@material-ui/styles';
import { DFSValueType, FormDefinitionWrapper, DFSExpression, NIFormData, parseFieldIdentifier } from '../services';
import { ErrorObject } from 'ajv';
import json_pointer, { has } from 'json-pointer';
import * as moment from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BehaviorSubject, combineLatest, from, merge, Observable, of } from 'rxjs';
import { catchError, concatMap, delay, distinctUntilChanged, filter, map, take, withLatestFrom } from 'rxjs/operators';
import { setActiveDefinition } from '../../../../redux';
import { IFormContext, FormContext, ImageService } from '../FormContext';
import { ExpressionManager } from '../services/ExpressionManager';
import { ValidityManager } from '../services/ValidityManager';
import { VisibilityManager } from '../services/VisibilityManager';
import { rescopeDataPath, resolveRefs } from '../utils';
import LoadingIndicator from './LoadingIndicator';
import { Page } from './Page';
import { TableOfContents } from './TableOfContents';
import EvalOverlay from '../../EvalOverlay';
import { InfoDialogProvider } from '../InfoDialogContext';
import fast_deep_equal from 'fast-deep-equal';


export interface FormWrapperProps {
  form: FormDefinitionWrapper;
  data$: Observable<NIFormData>;
  evalID?: string;
  canEdit?: boolean;
  onSetData: (path: string, scope: string, newData: DFSValueType | null) => void;
  onRemoveData: (path: string, scope: string) => void;
  onErrors?: (errors: ErrorObject[]) => void;
  compressedToCHeader?: JSX.Element;
  tocFooter?: JSX.Element;
  imageService: ImageService;
  scenarioName?: string;
}

export interface FormProps extends Omit<FormWrapperProps, 'form'> {
  form: FormDefinitionWrapper;
}

const drawerWidth = '240px';

const useStyles = makeStyles(function(theme: Theme) {
  return createStyles({
    root: {
      flex: 1,
      height: '100%',
      display: 'flex',
      overflow: 'auto',
      alignContent: 'stretch',
      backgroundColor: theme.palette.background.default,
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row'
      }
    },
    toolbar: {},
    menuButton: {
      color: theme.palette.getContrastText(theme.palette.primary.main)
    },
    drawer: {
      flexBasis: drawerWidth,
      zIndex: 0,
      height: '100%'
    },
    paper: {
      position: 'relative',
      top: 'inherit',
      left: 'inherit',
      bottom: 0,
      padding: 0,
      width: drawerWidth,
      zIndex: 1,
      borderRight: 0
    },
    drawerRoot: {
      display: 'flex',
      height: '100%',
      flexDirection: 'column'
    },
    tocHeader: {
      backgroundColor: theme.palette.primary.main
    },
    navFooter: {
      display: 'flex',
      flexDirection: 'column'
    },
    page: {
      flex: 1,
      overflowY: 'scroll' as 'scroll',
      WebkitOverflowScrolling: 'touch',
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column'
    }
  })
});

// todo use either redux or form context for active definition - not both
export const FormWrapper: FC<FormWrapperProps> = function(props) {
  const [form, setForm] = useState<FormDefinitionWrapper | undefined>(undefined);

  const dispatch = useDispatch();

  useEffect(function r() {
    if (props.form === undefined || props.form === null) {
      return;
    }

    const { definition, ...descriptorOnly } = props.form;
    dispatch(setActiveDefinition(descriptorOnly));

    const subscription = from(resolveRefs(props.form.definition))
      .pipe(
        catchError(function(e: any) {
          console.error('error resolving refs for definition', e);
          return of(props.form.definition);
        })
      )
      .subscribe(function(definition: any) {
        setForm({
          ...props.form,
          definition,
        });
      });

    return function() {
      dispatch(setActiveDefinition(null));
      return subscription.unsubscribe();
    }
  }, [props.form]);

  return (
    form === undefined || !props.form
      ? <LoadingIndicator>Loading...</LoadingIndicator>
      : <Form
          {...props}
          form = { form }>
        </Form>
  )
}

type TWatcher = [string, DFSValueType, DFSExpression, string]; // [dataPath, expResult, exp, scope]

type TDynamicComputedField = {
  dataPath: string;
  exp: DFSExpression;
  property: string;
  scopeRegex: RegExp;
}

// todo memoize this form via react.memo, remove useMemo from eval editor/follow up editor, move footer into own component and use directly here rather than passing as prop
const Form: FC<FormProps> = function(props: FormProps) {

  const { form, data$, onSetData, onRemoveData, evalID, imageService, canEdit, scenarioName } = props;
  const root = form.definition.render.editor;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentCard, setCurrentCard] = useState(0);
  const [pageOffsets, setPageOffsets] = useState<number[]>([]);

  const [pages, setPages] = useState(
    root.pages.map((def, idx) => ({ definition: def, index: idx, isVisible: true }))
  )

  const classes = useStyles({});

  const expMgr = useMemo(function() {
    return new ExpressionManager(data$);
  }, [data$]);

  const visibilityMgr = useMemo(function() {
    return new VisibilityManager(form.definition, expMgr);
  }, [form.id, expMgr]);

  const validityMgr = useMemo(function() {
    return new ValidityManager(form.definition, expMgr, data$ as any);
  }, [form.id, expMgr, data$]);

  useEffect(function() {
    return () => {
      expMgr.complete();
      visibilityMgr.complete();
      validityMgr.reset();
    }
  }, []);

  useEffect(function watchPageVisibility() {
    const subscription = combineLatest(root.pages.map((definition, index) => {
      const condition = definition.visibility ?? definition.visible;
      return condition !== undefined
        ? expMgr!.watch(condition)
            .pipe(
              map(isVisible => {
                return { definition, index, isVisible };
              })
            )
        : of({ definition, index, isVisible: true });
    }))
    .subscribe((pages: any[]) => {
      const hiddenPages: number[] = [];
      pages.forEach((page, i) => {
        if (!page.isVisible) {
          hiddenPages.push(i);
        }
      })
      setPageOffsets(hiddenPages);
      return setPages(pages);
    });

    return () => {
      return subscription.unsubscribe();
    };
  }, [expMgr, root.pages]);

  useEffect(function e() {

    if (!json_pointer.has(form.definition, '/hooks/edit/computedValues')) {
      return;
    }

    const addWatcher = (exp: DFSExpression, dataPath: string, scope = ''): Observable<TWatcher> => {
      return expMgr.watch(exp, scope).pipe(
        withLatestFrom(expMgr.watch(dataPath)),
        filter(function (e) {
          // [next, current]
          return e[0] !== e[1];
        }),
        map(function (e) {
          const [expResult, _] = e;
          return [dataPath, expResult, exp, scope];
        })
      )
    }

    /**
     * computedValues$ is an observable list of expressions which can be updated as the user fills up the evaluation
     * Initially, only simple computed values are defined, but as the user adds or deleted items in Subforms, any
     * expressions defined dynamically against array items are added to this list.
     */
    const computedValues$ = new BehaviorSubject<Observable<TWatcher>[]>([]);

    const computedFields = Object.entries(form.definition.hooks!.edit!.computedValues!);
    const staticComputedFields = computedFields.filter(function(computedFields) {      
      let [dataPath] = computedFields as [ string, DFSExpression ];
      return dataPath.includes('*') === false;
    });
    const dynamicComputedFields = computedFields.reduce((acc: TDynamicComputedField[], computedField) => {      
      let [dataPath, exp] = computedField as [ string, DFSExpression ];
      if (dataPath.includes('*') === true) {
        const parts = parseFieldIdentifier(dataPath).split('/*/');
        const computedFieldScope = parts.slice(0, -1).join('/*/') + '/*';
        const property = parts[parts.length-1];
        const scopeRegex = new RegExp('(' + computedFieldScope.replaceAll('*', '[0-9]+') + ')');
        acc.push({
          dataPath,
          exp,
          property,
          scopeRegex
        });        
      }
      return acc;
    }, []);

    // Initialization of static computed values
    const computers = staticComputedFields.map(function(computedFields) {      
      let [dataPath, exp] = computedFields as [ string, DFSExpression ];
      // watch existing path and expression. only update if expression result is different from current value
      return addWatcher(exp, dataPath);
    });
    computedValues$.next(computers);

    /**
     * Listens to any changes to the evaluation data
     * When an update happens:
     * - Build a list of all the key/value pairs, ie  [ {'/Prop1': 123}, {'/List1/0/prop1': 'abc'}, ...]
     * - Compare each item in that list against each dynamic computed value (with a '*' placeholder)
     * - Use a `property` variable (part of the path after the last '*')
     * - Set a `scope` variable (part of the path before the last '*')
     * - If not set yet, add a new watcher against that path ('/List1/0/prop1'), with the expression and the scope (so that relative expressions can work)
     * - Update the list of computedValues and re-emits it so the updated list of computed values can now be observed.
     */
    const dynamicComputedValuesSubscription = data$.pipe(
      concatMap(() => expMgr.getDict()),
      map(dict => Object.keys(dict)),
      distinctUntilChanged((a, b) => fast_deep_equal(a, b)),
      map((keys) => {
        const newComputedValues: Observable<TWatcher>[] = [];
        keys.forEach((key) => {
          dynamicComputedFields.forEach(function(computedFields) {
            const { scopeRegex, exp, property } = computedFields;              
            const scopeMatch = key.match(scopeRegex);
            const scope = scopeMatch ? scopeMatch[0] : '';              
            if (scope) {
              const rescoped = rescopeDataPath(`{${property}}`, scope);
              if (!expMgr.hasWatcher(rescoped)) {
                //console.log('Adding dynamic computed field', {rescoped})
                newComputedValues.push(addWatcher(exp, rescoped, scope));
              }
            }
          });
        });
        if (newComputedValues.length) {
          computedValues$.pipe(
            take(1),
            map(computers => {
              computedValues$.next([...computers, ...newComputedValues])
            })
          ).subscribe();
        }
      })
    ).subscribe();

    /**
     * Observe the list of computed values and update the data with the value of the expression
     * A 1ms delay is observed so that the updated evaluation data is used. This is important so that any deletion occurs prior to the recalculation 
     */
    const subscription = computedValues$.pipe(
      map((computers) => {
        return merge(...computers)
          .pipe(
            delay(1),
            withLatestFrom(data$)
          )
          .subscribe(function([e, data]) {
            const [ dataPath, val, exp, scope ] = e as TWatcher;
            
            // Do not set value against a non-existing scope, for example if a computed value is set against an array item, but that array item has been deleted by the user
            // as this would recreate the array item immediately after deleting it.
            if (scope && val && !has(data, scope)) {
              return ;
            }

            if (val !== undefined && val !== null) {
              onSetData(dataPath, '', val);
            }

            else {
              onRemoveData(dataPath, '');
            }
          });
      })
    ).subscribe();

    return function() {
      dynamicComputedValuesSubscription.unsubscribe();
      return subscription.unsubscribe()
    }
  }, [expMgr, form.id, onSetData, onRemoveData, form.definition, data$]);

  const [isDeskAudit, setIsDeskAudit] = React.useState(false);
  const [isFocusedAudit, setIsFocusedAudit] = React.useState(false);
  const [isReadonly, setIsReadonly] = React.useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = React.useState(false);

  const toggleDeskAudit = () => {
    setIsDeskAudit(!isDeskAudit);
  };
  const toggleFocusedAudit = () => {
    setIsFocusedAudit(!isFocusedAudit);
  };
  const toggleInfoDialogOpen = () => {
    setInfoDialogOpen(!infoDialogOpen);
  }

  const formContext:IFormContext = { form, expMgr, visibilityMgr, validityMgr, onSetData, onRemoveData, scope: '', evalID, imageService, isDeskAudit, toggleDeskAudit, isFocusedAudit, toggleFocusedAudit, isReadonly, setIsReadonly, infoDialogOpen, setInfoDialogOpen, toggleInfoDialogOpen };

  useEffect(() => {
    if (canEdit || canEdit === false) {
      setIsReadonly(!canEdit);
    }
  }, [canEdit]);

  useEffect(function r() {
    if (!props.onErrors) {
      return;
    }
    const subscription = formContext.validityMgr!.watch('').subscribe(function(r) {
      props.onErrors!(r)
    });
    return function() {
      return subscription.unsubscribe()
    }
  }, [props.onErrors, formContext.validityMgr]);

  // if for some reason a page that is out of range is selected, just go to the first page.
  useEffect(() => {
    if (root.pages && !root.pages[currentPage] && currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [root, currentPage])

  const onPageSelected = (page: number) => {
    setCurrentPage(page);
    setCurrentCard(-1);
    handleDrawerToggle();
  };

  const onCardSelected = (card: number) => {
    setCurrentCard(card);
    handleDrawerToggle();
  };

  const toc = useMemo(function() {
    return <TableOfContents
      pages = { pages }
      currentPage = { currentPage }
      currentCard = { currentCard }
      onPageSelected = { onPageSelected }
      onCardSelected = { onCardSelected }>
      </TableOfContents>
  }, [root.pages, currentPage, currentCard, onPageSelected, onCardSelected]);

  const footer = <div className = { classes.navFooter }>
    { props.tocFooter &&
      <React.Fragment>
        <Divider variant = { 'fullWidth' }></Divider>
        { props.tocFooter }
      </React.Fragment>
    }
  </div>;

  const handleDrawerToggle = useMemo(function() {
    return function() {
      return setDrawerOpen(!drawerOpen)
    }
  }, [drawerOpen]);

  return (
    <FormContext.Provider value = { formContext }>
      <EvalOverlay></EvalOverlay>
      <InfoDialogProvider>
        <MuiPickersUtilsProvider libInstance = { moment } utils = { MomentUtils }>
          <div className = { classes.root }>
            <Hidden mdUp>
              <AppBar className = { classes.toolbar }>
                <Toolbar>
                  <IconButton onClick = { handleDrawerToggle }>
                    <Menu color = { 'inherit' } className = { classes.menuButton } />
                  </IconButton>
                </Toolbar>
              </AppBar>
              <Drawer
                className = { classes.drawer }
                variant = { 'temporary' }
                classes = { { paper: classes.paper } }
                anchor = { 'left' }
                open = { drawerOpen }
                onClose = { handleDrawerToggle }
                ModalProps = { { keepMounted: true } }>
                <div className = { classes.drawerRoot }>
                  <div style = { { overflow: 'scroll' } }>
                    { props.compressedToCHeader &&
                      <div className = { classes.tocHeader }>
                        { props.compressedToCHeader }
                      </div>
                    }
                    { toc }
                  </div>
                  { footer }
                </div>
              </Drawer>
            </Hidden>

            <Hidden smDown implementation = { 'css' }>
              <Drawer
                className = { classes.drawer }
                variant = { 'permanent' }
                classes = { { paper: classes.paper } }>
                { toc }
                { footer }
              </Drawer>
            </Hidden>
            <div className = { classes.page }>
              <Page
                path = { '/render/editor/pages/' + currentPage }
                definition = { root.pages[currentPage] }
                activeCard = { currentCard }
                page = { currentPage }
                pageCount = { root.pages.length }
                pages = { pages }
                pageOffsets = { pageOffsets }
                scenarioName = {scenarioName}
                onChangePage = { e => {
                  setCurrentPage(e);
                  setCurrentCard(-1);
                } }
                ></Page>
            </div>
          </div>
        </MuiPickersUtilsProvider>
      </InfoDialogProvider>
    </FormContext.Provider>
  )
};
